import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { injectIntl } from 'react-intl';
import { emptyObject } from 'ui-components/default-props/empty';
import Global from '../../../store/global-selectors';
import BillToAccount from './inputs/bill-to-account/bill-to-account';
import { canUseFedExGroundCollect } from '../../../services/other-shipping-options/helpers';
import OptionsConstants from '../../../constants/other-shipping-options-constants';
import { BULK_UPDATE_FULFILLMENT_PLAN_ID } from '../constants';
import messages from '../messages';

// TODO: update the bulk update other options modal to also work with seller shipping accounts
const bulkUpdateShim = (optionData, intl) => {
  return {
    activeParty: optionData.billToParty,
    checkBoxLabel: intl.formatMessage(messages.default),
    isBulkUpdate: true,
    thirdPartyBillingIsAllowed: optionData !== null,
  };
};

const sellerLabelProvidersWithMatchingCarrierIdsSelector = createSelector(
  (allLabelProviders) => allLabelProviders,
  (_, sellerLabelProviders) => sellerLabelProviders,
  (_, __, servicesCarrierId) => servicesCarrierId,
  (allLabelProviders, sellerLabelProviders, servicesCarrierId) => {
    const filteredReferenceProviders = allLabelProviders.filter((lp) => lp.carrierId === servicesCarrierId);
    const simpleReferenceProviders = filteredReferenceProviders.map((lp) => lp.labelProviderId);
    return sellerLabelProviders.filter((slp) => {
      return simpleReferenceProviders.includes(slp.labelProviderId);
    });
  },
);

const defaultSellerLabelProvidersSelector = createSelector(
  (labelProviderId) => labelProviderId,
  (_, sellerLabelProviders) => sellerLabelProviders,
  (_, __, labelProviderName) => labelProviderName,
  (_, __, ___, intl) => intl,
  (_, sellerLabelProviders, labelProviderName, intl) => {
    const matchingProviders = sellerLabelProviders.map((slp, index) => {
      // spread into a new object so you don't mutate the reference data array
      const copiedSlp = { ...slp };
      if (!slp.name && slp.accountNumber) {
        copiedSlp.name = `${labelProviderName} - ${slp.accountNumber}`;
      }

      if (!slp.name && !slp.accountNumber && !slp.isPrimaryAccount) {
        copiedSlp.name = `${labelProviderName} ${index}`;
      }

      if (!slp.name && !slp.accountNumber && slp.isPrimaryAccount) {
        copiedSlp.name = labelProviderName;
      }

      if (slp.isPrimaryAccount) {
        copiedSlp.name = intl.formatMessage(messages.primaryAccount, {
          accountName: copiedSlp.name,
        });
      }

      return copiedSlp;
    });
    const defaultProvider = matchingProviders.find((slp) => slp.isPrimaryAccount);
    if (!defaultProvider) {
      return [];
    }
    return [...new Set([defaultProvider, ...matchingProviders])];
  },
);

const findMatchingServiceObject = (state, selectedServiceId) => {
  const sellerServices = Global.selectSellerServices(state);
  return (
    sellerServices.find((service) => {
      return service.serviceId === selectedServiceId;
    }) || { labelProviderId: '', optionCodes: [], serviceId: '' }
  );
};

const isThirdPartyBillingActive = (selectedServiceOptionCodes, optionData) => {
  const thirdPartyBillingIsAllowed = selectedServiceOptionCodes.includes(OptionsConstants.billToThirdParty);
  const thirdPartyBillingIsActive = optionData && optionData.billToParty;
  return {
    hasBillToAccountSelected: thirdPartyBillingIsAllowed && thirdPartyBillingIsActive,
    thirdPartyBillingIsAllowed,
  };
};

const mapStateToProps = (state, { fulfillmentPlanId, optionData, intl, selectedServiceId, selectedSellerLabelProviderId }) => {
  if (fulfillmentPlanId === BULK_UPDATE_FULFILLMENT_PLAN_ID && optionData) {
    return bulkUpdateShim(optionData, intl);
  }

  const serviceObject = findMatchingServiceObject(state, selectedServiceId);

  const allLabelProviders = Global.selectReferenceDatagetProviders(state);
  const { name: labelProviderName = '' } = allLabelProviders.find((lp) => lp.labelProviderId === serviceObject.labelProviderId) || {};
  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  const filteredSellerLabelProviders = sellerLabelProvidersWithMatchingCarrierIdsSelector(
    allLabelProviders,
    sellerLabelProviders,
    serviceObject.carrierId,
  );
  const selectedSellerLabelProviders = defaultSellerLabelProvidersSelector(
    serviceObject.labelProviderId,
    filteredSellerLabelProviders,
    labelProviderName,
    intl,
  );

  const selectedSellerLabelProvider =
    selectedSellerLabelProviders.find((slp) => slp.sellerLabelProviderId === selectedSellerLabelProviderId) || emptyObject;
  const selectedOptionData =
    Global.selectSellerServiceBySellerLabelProviderId(state, {
      sellerLabelProviderId: selectedSellerLabelProvider.sellerLabelProviderId,
    }) || serviceObject;
  const { hasBillToAccountSelected, thirdPartyBillingIsAllowed } = isThirdPartyBillingActive(selectedOptionData.optionCodes, optionData);
  const fedExGroundCollectAvailable = canUseFedExGroundCollect(selectedServiceId);
  const activeParty = hasBillToAccountSelected ? optionData.billToParty : selectedSellerLabelProvider.name;

  return {
    activeParty: activeParty ? activeParty.toString() : '',
    canUseFedExGroundCollect: fedExGroundCollectAvailable,
    carrierId: serviceObject.carrierId,
    fulfillmentPlanId,
    selectedSellerLabelProviderId: selectedSellerLabelProvider.sellerLabelProviderId,
    selectedSellerLabelProviderName: selectedSellerLabelProvider.name,
    sellerLabelProviders: selectedSellerLabelProviders,
    thirdPartyBillingIsAllowed,
  };
};

export default injectIntl(connect(mapStateToProps)(BillToAccount));
